import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';
import UpcomingEventCard from '../components/events/UpcomingEventCard';
import { eventService } from '../services/eventService';

const PAGE_SIZE = 12;

const mapApiEvent = (e) => ({
  id: e.id,
  slug: e.slug,
  title: e.name || e.title,
  date: e.start_date
    ? new Date(e.start_date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
      })
    : '',
  time: e.start_time ? e.start_time.slice(0, 5) : '',
  city: e.city || '',
  venue: e.venue || '',
  imageUrl: e.primary_image || e.images?.[0]?.original_image || '',
  category: e.category_display || e.category || '',
  priceLabel: e.is_free ? 'Free' : e.price ? `Rs. ${Number(e.price).toLocaleString()}` : 'Paid',
});

const buildDateStrip = (offset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + offset + i);
    days.push(d);
  }
  return days;
};

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const Pagination = ({ current, total, onChange }) => {
  if (total <= 1) return null;

  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push('...');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);
  }

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, current - 1))}
        disabled={current === 1}
        className="w-9 h-9 rounded-full flex items-center justify-center border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
      </button>

      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`e-${i}`} className="w-9 h-9 flex items-center justify-center text-gray-400 text-sm">...</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`w-9 h-9 rounded-full text-sm font-semibold transition-colors ${
              current === p
                ? 'bg-emerald-700 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            {p}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(Math.min(total, current + 1))}
        disabled={current === total}
        className="w-9 h-9 rounded-full flex items-center justify-center border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
      </button>
    </div>
  );
};

const EventsListPage = ({ timeFrame }) => {
  const isUpcoming = timeFrame === 'upcoming';
  const pageTitle = isUpcoming ? 'Upcoming Events' : 'Recent Events';

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [dateStripOffset, setDateStripOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const datePickerRef = useRef(null);
  const dateStripDays = useMemo(() => buildDateStrip(dateStripOffset), [dateStripOffset]);

  const loadEvents = useCallback(async (pg, dateFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page_size: PAGE_SIZE,
        page: pg,
        time_frame: timeFrame,
        ordering: isUpcoming ? 'start_date' : '-start_date',
      };
      if (dateFilter) params.start_date = dateFilter;
      const res = await eventService.getEvents(params);
      const data = res?.results ?? res?.data?.results ?? res?.data ?? res;
      const count = res?.count ?? res?.data?.count ?? 0;
      setEvents(Array.isArray(data) ? data.map(mapApiEvent) : []);
      setTotalCount(count);
      setTotalPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
    } catch {
      setEvents([]);
      setTotalPages(1);
      setTotalCount(0);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [timeFrame, isUpcoming]);

  useEffect(() => {
    setPage(1);
    setSelectedDate(null);
    loadEvents(1, null);
  }, [loadEvents]);

  const handlePageChange = useCallback((pg) => {
    setPage(pg);
    loadEvents(pg, selectedDate);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loadEvents, selectedDate]);

  const handleDateSelect = useCallback((d) => {
    const iso = toISODate(d);
    if (selectedDate === iso) {
      setSelectedDate(null);
      setPage(1);
      loadEvents(1, null);
    } else {
      setSelectedDate(iso);
      setPage(1);
      loadEvents(1, iso);
    }
  }, [selectedDate, loadEvents]);

  const handleDatePickerChange = useCallback((e) => {
    const val = e.target.value;
    setPage(1);
    if (!val) {
      setSelectedDate(null);
      loadEvents(1, null);
    } else {
      setSelectedDate(val);
      loadEvents(1, val);
    }
  }, [loadEvents]);

  const clearDateFilter = useCallback(() => {
    setSelectedDate(null);
    setPage(1);
    loadEvents(1, null);
  }, [loadEvents]);

  return (
    <PageLayout title="PakPoints">
      <Helmet>
        <title>{pageTitle} - PakPoints</title>
        <meta name="description" content={`Browse all ${pageTitle.toLowerCase()} across Pakistan.`} />
      </Helmet>

      <div className="bg-[#f7f6f3] min-h-screen">

        {/* ═══ Header ═══ */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
              <h1 className="text-2xl sm:text-3xl text-gray-900">
                <span className="font-bold">{pageTitle}</span>{' '}
                <span className="font-normal">in Pakistan</span>
              </h1>
              <nav className="text-xs text-gray-400" aria-label="Breadcrumb">
                <ol className="flex items-center gap-1.5">
                  <li className="flex items-center gap-1">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                    </svg>
                    <Link to="/" className="hover:text-gray-600">Home</Link>
                  </li>
                  <li>/</li>
                  <li><Link to="/events" className="hover:text-gray-600">Events</Link></li>
                  <li>/</li>
                  <li className="text-gray-600 font-medium">{pageTitle}</li>
                </ol>
              </nav>
            </div>
            <p className="text-gray-400 text-sm">
              {isUpcoming
                ? 'Browse all upcoming events across Pakistan.'
                : 'Explore events that have already taken place.'}
            </p>
          </div>
        </div>

        {/* ═══ Content ═══ */}
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* Date filter (upcoming only) */}
          {isUpcoming && (
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <input
                ref={datePickerRef}
                type="date"
                className="sr-only"
                value={selectedDate || ''}
                onChange={handleDatePickerChange}
                tabIndex={-1}
                aria-label="Pick a date"
              />

              <button
                type="button"
                onClick={() => datePickerRef.current?.showPicker()}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                {selectedDate ? selectedDate : 'Pick a date'}
                <span className="text-gray-400 hidden sm:inline">Filter by date</span>
              </button>

              {selectedDate && (
                <button
                  type="button"
                  onClick={clearDateFilter}
                  className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  Clear
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}

              <button type="button" onClick={() => setDateStripOffset((o) => o - 7)} className="w-7 h-7 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 flex items-center justify-center">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
              </button>

              <div className="flex gap-1 overflow-x-auto scrollbar-hide">
                {dateStripDays.map((d) => {
                  const iso = toISODate(d);
                  const isActive = selectedDate === iso;
                  return (
                    <button
                      key={iso}
                      type="button"
                      onClick={() => handleDateSelect(d)}
                      className={`flex-shrink-0 w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                        isActive
                          ? 'bg-gray-800 text-white'
                          : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>

              <button type="button" onClick={() => setDateStripOffset((o) => o + 7)} className="w-7 h-7 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 flex items-center justify-center">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
              </button>
            </div>
          )}

          {/* Results summary */}
          {!loading && totalCount > 0 && (
            <p className="text-xs text-gray-400 mb-4">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} events
            </p>
          )}

          {/* Error */}
          {error && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">{error}</div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
            </div>
          )}

          {/* Empty */}
          {!loading && !error && events.length === 0 && (
            <div className="text-center py-16">
              <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
              <p className="text-sm text-gray-500">
                {isUpcoming
                  ? (selectedDate ? 'No events found for this date.' : 'No upcoming events at the moment.')
                  : 'No recent events to show.'}
              </p>
              <Link to="/events" className="inline-block mt-4 text-sm text-emerald-700 hover:text-emerald-800 font-medium">
                &larr; Back to Events
              </Link>
            </div>
          )}

          {/* Grid */}
          {!loading && events.length > 0 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {events.map((event) => (
                  <UpcomingEventCard key={event.id} event={event} showAddToCalendar={isUpcoming} />
                ))}
              </div>

              <Pagination current={page} total={totalPages} onChange={handlePageChange} />
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default EventsListPage;
